import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, Row } from "reactstrap";
import { useParams } from "react-router-dom";
import { emptyBpmn } from "./emptyBpmn/emptyBpmn";
import { showAlertServiceError } from "utils/alerts";
import { useTranslation } from "react-i18next";
import { loaderElement } from "utils/loaderElement";
import "bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-font/dist/css/bpmn-embedded.css";
import bpmnlintConfig from "./bundled-config";
import BpmnModeler from "bpmn-js/lib/Modeler";
import lintModule from "bpmn-js-bpmnlint";
import customContextPadProvider from "./customContextPadProvider";
import useWorkFlowsDiagram from "hooks/useWorkflowsDiagram";
import customPaletteProvider from "./custom/customPaletteProvider";
import Swal from "sweetalert";

const BpmnComponent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { upload_xml } = useWorkFlowsDiagram();
  const [hasErrors, setHasErrors] = useState(true);
  const [isLoadingDiagram, setIsLoadingDiagram] = useState(false);
  const bpmContainerRef = useRef(null);
  const modelerRef = useRef(null);

  /**
   * Initializes the BPMN modeler instance on component mount.
   * This effect:
   * - Creates a new BpmnModeler instance with custom modules and linting configuration.
   * - Attaches a listener to handle linting results and update error state.
   * - Imports an empty BPMN diagram and adjusts the canvas to fit the viewport.
   * - Triggers initial lint validation after import.
   * - Cleans up by destroying the modeler instance on component unmount.
   * @param {React.MutableRefObject<HTMLElement|null>} bpmContainerRef - Reference to the DOM container where the BPMN modeler is mounted.
   * @param {React.MutableRefObject<Object|null>} modelerRef - Reference used to persist the modeler instance.
   * @param {Object} bpmnlintConfig - Configuration object for BPMN linting rules.
   * @param {Object} customContextPadProvider - Custom context pad module for BPMN modeler.
   * @param {Object} lintModule - BPMN linting module.
   * @param {Object} customPaletteProvider - Custom palette provider module.
   * @param {string} emptyBpmn - Initial BPMN XML string to load into the modeler.
   * @param {Function} setHasErrors - State setter function to update linting error status.
   * @returns {void} This effect does not return a value. Cleanup destroys the modeler instance.
   */
  useEffect(() => {
    const modeler = new BpmnModeler({
      container: bpmContainerRef.current,
      keyboard: { bindTo: window },
      linting: {
        bpmnlint: bpmnlintConfig,
      },
      additionalModules: [customContextPadProvider, lintModule, customPaletteProvider],
    });

    modelerRef.current = modeler;

    modeler.on("linting.completed", (event) => {
      const issues = Object.values(event.issues || {}).flat();
      setHasErrors(issues.length > 0);
    });

    modeler.importXML(emptyBpmn).then(() => {
      const canvas = modeler.get("canvas");
      canvas.zoom("fit-viewport");

      // Run initial validation
      const eventBus = modeler.get("eventBus");
      eventBus.fire("linting.validate");
    });

    return () => {
      modeler.destroy();
    };
  }, []);

  /**
   * Triggers BPMN diagram validation using the modeler linting service.
   * If the modeler instance is available, it ensures that linting
   * is active and then fires the validation event to analyze the diagram.
   * @function validateDiagram
   * @returns {void} Does nothing if the modeler reference is not available.
   */
  const validateDiagram = () => {
    if (!modelerRef.current) return;

    const modeler = modelerRef.current;
    const linting = modeler.get("linting");
    const eventBus = modeler.get("eventBus");

    if (!linting.isActive()) {
      linting.toggle();
    }

    // ONLY validate
    eventBus.fire("linting.validate");
  };

  /**
   * Saves the current BPMN diagram as an XML file and uploads it to the server.
   * This function:
   * - Prevents saving if the modeler reference is not available.
   * - Stops execution if validation errors exist.
   * - Exports the diagram as formatted XML.
   * - Prompts the user for confirmation before uploading.
   * - Uploads the generated XML file using FormData.
   * - Displays success or error alerts based on the server response.
   * @async
   * @function saveDiagram
   * @returns {Promise<void>} Resolves when the save and upload process completes.
   */
  const saveDiagram = async () => {
    if (!modelerRef.current) return;

    //  If there are errors, do not save
    if (hasErrors) {
      Swal({
        title: t("modal.DoneError.header"),
        text: t("createWorkflowDesign.modalError"),
        icon: "error",
        button: t("modal.Done.footerButton"),
      });
      return;
    }

    const modeler = modelerRef.current;

    // Save directly
    const { xml } = await modeler.saveXML({ format: true });

    Swal({
      title: t("modal.DoneError.header"),
      text: t("createWorkflowDesign.modalRequest"),
      icon: "warning",
      buttons: [t("createWorkflowDesign.cancel"), t("createWorkflowDesign.saveModal")],
    })
      .then(async (result) => {
        setIsLoadingDiagram(true);
        if (!result) return;

        const file = new File([xml], "diagram.xml", {
          type: "text/xml",
          lastModified: new Date().getTime(),
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("size", file.size);

        const res = await upload_xml(formData, id);

        if (res?.data?.code === 5015) {
          setIsLoadingDiagram(false);
          Swal({
            title: t("modal.DoneError.header"),
            text: t("createWorkflowDesign.modalSuccess"),
            icon: "success",
            button: t("modal.Done.footerButton"),
          }).then(() => {
            window.location = "/workflow/list";
          });
        } else {
          showAlertServiceError();
          setIsLoadingDiagram(false);
        }
      })
      .finally(() => setIsLoadingDiagram(false));
  };

  return (
    <Fragment>
      {loaderElement(isLoadingDiagram)}
      <div className="bpmn-wrapper">
        <Row>
          <Button color="primary" onClick={saveDiagram} className="ml-3" disabled={hasErrors}>
            {t("createWorkflowDesign.save")}
          </Button>

          <Button color="success" onClick={validateDiagram} className="ml-3">
            {t("createWorkflowDesign.validate")}
          </Button>

          <div className="ml-auto mr-3">
            <Button color="danger" onClick={() => (window.location = "/workflow/list")}>
              ✕
            </Button>
          </div>
        </Row>

        <div className="bpmn-modeler-view" ref={bpmContainerRef} />
      </div>
    </Fragment>
  );
};

export default BpmnComponent;
